import { AppointmentsPage, type AppointmentsCompanion } from "@internal/telemedicine"
import React from "react"
import { useIntl } from "react-intl"
import { useCarrotCompanion } from "#/components/care-team/hooks/useCarrotCompanion"
import { formatCarrotCompanionName } from "#/components/care-team/utils/carrotCompanionNameUtil"
import { OtherWaysToContactCarrotTile } from "#/components/care-team/OtherWaysToContactCarrotTile"
import { PageHeader } from "#/features/global-ui/page-header"
import { PageLayout } from "#/features/global-ui/page-layout"
import { Paths } from "#/utils/Paths"
import { useBookingActionTracking } from "#/services/memberActionTracking/BookingTrackingProvider"
import { MemberActions } from "#/services/memberActionTracking/memberActions"
import { useMemberActionTracking } from "#/services/memberActionTracking/useMemberActionTracking"

/**
 * Host wrapper for the Appointments page, which lives in `@internal/telemedicine` (CONAPP-276).
 *
 * Sits in `src/pages/telemedicine/` beside `booking/`, `resolver/` and the Contentful-driven pages,
 * because that is where this package's host pages live — not under `src/pages/consumer/`.
 *
 * The split is the one the package's other pages already use — `TelemedicineBookingPage` owns
 * `PageLayout` while `BookingFlow` owns the feature. So the chrome and everything that reaches into
 * the host stays here; the route itself is declared in `App.tsx` (via `AppointmentsRoute`, which
 * holds the `isConsumer` gate), exactly like the package's other routes. The package gets props:
 *
 * - `bookingPath`, because `Paths` is a host module.
 * - `companion`, because `useCarrotCompanion` reads the monolithic `carrotClient` and the host's user
 *   context, and `formatCarrotCompanionName` is the helper every other care-team surface formats with.
 *
 * Imported statically, like the package's other pages. No `React.lazy`: the barrel is already in the
 * eager App chunk (`src/pages/consumer/home/page.tsx` imports `useTelemedicineAccess` from it), so a
 * lazy boundary here would defer nothing.
 */
export function TelemedicineAppointmentsPage(): JSX.Element {
  const { trackBookingEntry } = useBookingActionTracking()
  const { mutate: trackMemberAction } = useMemberActionTracking()
  const trackAppointmentsEvent = (memberActionTaken: MemberActions, metaData: object): void => {
    try {
      trackMemberAction({ memberActionTaken, metaData })
    } catch {
      // Tracking cannot interrupt an appointment action or change its result.
    }
  }
  const intl = useIntl()
  // The hook returns no bio yet — see the TODO in the package's `CarrotCompanionDetails`.
  const { data: carrotCompanion } = useCarrotCompanion()
  const { displayName } = formatCarrotCompanionName(carrotCompanion?.name)
  // Null rather than a half-built object while the query is in flight, so the package has one thing
  // to check instead of three possibly-empty fields.
  const companion: AppointmentsCompanion | null = carrotCompanion
    ? { name: carrotCompanion.name, photoUrl: carrotCompanion.photoUrl ?? null, displayName }
    : null

  // No `<Title>`: `Paths.TELEMEDICINE_APPOINTMENTS` is already in the map in
  // `src/services/page-title/Title.tsx`, matched by prefix, so the browser tab is handled.
  const pageTitle = intl.formatMessage({ defaultMessage: "Appointments" })

  return (
    <PageLayout
      variant="sidebar"
      header={<PageHeader pageTitle={pageTitle} />}
      sidebar={<OtherWaysToContactCarrotTile />}
    >
      <AppointmentsPage
        bookingPath={Paths.TELEMEDICINE_BOOKING}
        companion={companion}
        onBookAppointment={(event) => trackBookingEntry(event, "appointments_page")}
        onPrepareForVisit={(appointmentId) =>
          trackAppointmentsEvent(MemberActions.APPOINTMENTS_PREPARE_FOR_VISIT_CLICKED, { appointmentId })
        }
        onTrackEvent={({ type, ...metaData }) => trackAppointmentsEvent(MemberActions[type], metaData)}
      />
    </PageLayout>
  )
}
